import { useState } from "react";
import { useForm, FieldValues } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import PerformerService, { Performer } from "@/app/_services/PerformerService";
import { performerSchema } from "./PerformerSchema";
import { toast } from "@/app/_utils/toast";

type FormData = yup.InferType<typeof performerSchema>;

export function usePerformerFormFlow({
  defaultPerformer,
  onSaved,
}: {
  defaultPerformer?: Performer | null;
  onSaved: () => void;
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(performerSchema),
    values: defaultPerformer
      ? {
          first_name: defaultPerformer.first_name,
          last_name: defaultPerformer.last_name,
          instagram: defaultPerformer.instagram ?? "",
          email: defaultPerformer.email ?? "",
          phone: defaultPerformer.phone ?? "",
        }
      : {
          first_name: "",
          last_name: "",
          instagram: "",
          email: "",
          phone: "",
        },
  });

  const handleSave = handleSubmit(async (fields: FieldValues) => {
    setIsSaving(true);
    const payload = selectedImage ? { ...fields, image: selectedImage } : fields;
    try {
      if (defaultPerformer) {
        await PerformerService.updatePerformerAsync(defaultPerformer.id, payload);
        toast.success("Performer updated");
      } else {
        await PerformerService.createPerformerAsync(payload);
        toast.success("Performer created");
        reset({ first_name: "", last_name: "", instagram: "", email: "", phone: "" });
      }
      setSelectedImage(null);
      onSaved();
    } catch (e) {
      toast.error("Failed to save performer");
    } finally {
      setIsSaving(false);
    }
  });

  return {
    control,
    errors,
    isSaving,
    selectedImage,
    setSelectedImage,
    handleSave,
  };
}
